import { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { HosDriverDto } from "../data/service-proxies";
import { AppState } from "../types";
import { addFleetManagerHubReconnectHandler } from "./fleetManagerHubReconnectHandlers";
import { useSelectedTenant } from "./useSelectedTenant";

type RefreshHosData = () => void | Promise<void>;

export const useFleetManagerHubHosUpdates = (
  driverId: number | undefined,
  refreshHosData: RefreshHosData
) => {
  const { hub } = useSelector((x: AppState) => x.fleetManagerHub);
  const { tenantId } = useSelectedTenant();

  const handleHosData = useCallback(
    (data: HosDriverDto) => {
      if (data.id !== driverId && data.driverIntId !== driverId) {
        return;
      }

      void refreshHosData();
    },
    [driverId, refreshHosData]
  );

  useEffect(() => {
    if (!hub || !driverId) {
      return;
    }

    const subscription = { tenantId, driverId };
    const subscribe = () => {
      hub.invoke("SubscribeToDriver", subscription).catch(console.error);
    };
    const handleReconnected = () => {
      subscribe();
      void refreshHosData();
    };

    subscribe();
    hub.on("onHosDataRefreshed", handleHosData);
    const removeReconnectHandler = addFleetManagerHubReconnectHandler(
      hub,
      handleReconnected
    );

    return () => {
      hub.off("onHosDataRefreshed", handleHosData);
      removeReconnectHandler();
      hub.invoke("UnsubscribeFromDriver", subscription).catch(console.error);
    };
  }, [hub, handleHosData, tenantId, driverId, refreshHosData]);
};
